import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import type { User } from '@claude-code-api/shared';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (provider: 'google' | 'oidc') => void;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  isApproved: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const metaEnv =
  (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env || {};

const trim = (value: string | undefined): string => String(value || '').trim();
const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const explicitOidcLogoutUrl = trim(metaEnv.VITE_AUTH_ENGINE_LOGOUT_URL);
const oidcLogoutUrl = isHttpUrl(explicitOidcLogoutUrl)
  ? explicitOidcLogoutUrl
  : '';

async function fetchCurrentUser(): Promise<User | null> {
  // Retry a few times in case the API server is still starting up
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/auth/me', {
        credentials: 'include',
        signal: controller.signal,
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      clearTimeout(timeout);

      if (!response.ok) return null;
      const data = (await response.json()) as { data?: User };
      return data.data ?? null;
    } catch (error) {
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 1000));
        continue;
      }
      console.error('Failed to fetch current user after retries:', error);
      return null;
    }
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void refreshUser().finally(() => setLoading(false));
  }, []);

  const login = (provider: 'google' | 'oidc') => {
    if (provider === 'oidc') {
      // Backend-centered auth flow: OIDC callback creates the same userId cookie used by API guard.
      window.location.href = '/api/auth/oidc';
      return;
    }

    window.location.href = `/api/auth/${provider}`;
  };

  const logout = async () => {
    let logoutError: unknown = null;
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      logoutError = error;
    } finally {
      setUser(null);
    }
    if (oidcLogoutUrl) {
      window.location.href = oidcLogoutUrl;
      return;
    }
    if (logoutError) throw logoutError;
  };

  const refreshUser = async (): Promise<User | null> => {
    try {
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      return null;
    }
  };

  const isApproved = user?.isApproved ?? false;
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refreshUser, isApproved, isAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
